import React, { useEffect, useState, useCallback, useMemo } from "react";
import { SiReact, SiJavascript, SiNodedotjs, SiMongodb } from "react-icons/si";
import { BsKeyFill } from "react-icons/bs";
import {
  FaDownload,
  FaLock,
  FaShieldAlt,
  FaCode,
  FaUserShield,
  FaBrain,
  FaCalendarAlt,
  FaArrowRight,
  FaAward,
  FaRocket,
  FaRegClock,
} from "react-icons/fa";
import waisImage1 from "../assets/images/profile pic.png";
import { skillsData, certificationsData } from "../data/portfolioData";

// ==================== OPTIMIZED REUSABLE COMPONENTS ====================

const CertificationsList = ({ certifications }) => (
  <div className="space-y-0">
    {certifications.map((cert, index) => (
      <div
        key={index}
        className="flex items-center justify-between p-1 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-all duration-200 hover:translate-x-1 cursor-pointer"
      >
        <div>
          <div className="text-sm font-medium text-gray-900 dark:text-white">
            {cert.title}
          </div>
          <div className="text-xs text-gray-600 dark:text-gray-400">
            {cert.issuer}
          </div>
        </div>
        <div className="text-xs text-emerald-600 dark:text-cyan-400 font-medium">
          {cert.date}
        </div>
      </div>
    ))}
  </div>
);

// Modern Expertise Card with 3D tilt effect
const ExpertiseCard = ({ icon, title, desc, color, lightBorder, index }) => {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <section id="home"
      className="group relative rounded-xl bg-gradient-to-br from-white to-white/90 dark:from-gray-800/80 dark:to-gray-900/40 backdrop-blur-sm border dark:border-gray-700/50 p-4 transition-all duration-500 hover:shadow-2xl hover:shadow-cyan-500/20 hover:scale-[1.02] hover:-translate-y-1 cursor-pointer"
      style={{
        borderColor: lightBorder?.replace("border-", "") || "inherit",
        transitionTimingFunction: "cubic-bezier(0.4, 0, 0.2, 1)",
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div className="absolute inset-0 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none">
        <div
          className={`absolute inset-0 bg-gradient-to-r ${color} rounded-xl -z-10 animate-pulse-slow`}
        />
      </div>

      <div className="absolute inset-0 rounded-xl overflow-hidden pointer-events-none">
        <div className="absolute inset-0 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-1000 bg-gradient-to-r from-transparent via-white/20 to-transparent" />
      </div>

      <div className="relative flex items-start gap-3">
        <div
          className={`flex-shrink-0 p-2 rounded-lg bg-gradient-to-r ${color} text-white shadow-sm transition-all duration-300 ${isHovered ? "scale-110 rotate-3" : "scale-100"}`}
        >
          {icon}
        </div>

        <div className="flex-1 min-w-0">
          <h4 className="text-sm md:text-base font-semibold text-gray-900 dark:text-white mb-1 transition-colors duration-300 group-hover:text-transparent group-hover:bg-clip-text group-hover:bg-gradient-to-r group-hover:from-emerald-600 group-hover:to-cyan-500">
            {title}
          </h4>
          <p className="text-gray-600 dark:text-gray-400 text-xs leading-relaxed transition-opacity duration-300 group-hover:text-gray-700 dark:group-hover:text-gray-300">
            {desc}
          </p>
        </div>
      </div>
    </section>
  );
};

// Modern Skills Tags with micro-interactions
const SkillsTags = ({ skills, category, hasMore, expanded, onToggle }) => {
  const colorMap = useMemo(
    () => ({
      webDev:
        "from-emerald-500/15 to-emerald-500/5 text-emerald-700 dark:text-emerald-400 border-emerald-300 dark:border-emerald-500/20 hover:from-emerald-500/25 hover:to-emerald-500/10",
      cybersecurity:
        "from-purple-500/15 to-purple-500/5 text-purple-700 dark:text-purple-400 border-purple-300 dark:border-purple-500/20 hover:from-purple-500/25 hover:to-purple-500/10",
      devOps:
        "from-cyan-500/15 to-cyan-500/5 text-cyan-700 dark:text-cyan-400 border-cyan-300 dark:border-cyan-500/20 hover:from-cyan-500/25 hover:to-cyan-500/10",
      graphicDesign:
        "from-pink-500/15 to-pink-500/5 text-pink-700 dark:text-pink-400 border-pink-300 dark:border-pink-500/20 hover:from-pink-500/25 hover:to-pink-500/10",
    }),
    [],
  );

  const colorClass = colorMap[category] || colorMap.webDev;

  return (
    <div className="flex flex-wrap gap-1.5">
      {skills.map((skill, idx) => (
        <span
          key={skill.name}
          className={`px-2 py-1 text-xs font-medium rounded-full bg-gradient-to-r ${colorClass} border transition-all duration-200 hover:scale-105 hover:shadow-md cursor-pointer animate-slide-in`}
          style={{ animationDelay: `${idx * 30}ms` }}
        >
          {skill.name}
        </span>
      ))}
      {hasMore && !expanded && (
        <button
          onClick={onToggle}
          className="px-2 py-1 text-xs font-extrabold tracking-wider rounded-full bg-gradient-to-r from-gray-500/15 to-gray-500/5 text-gray-700 dark:text-gray-400 border border-gray-300 dark:border-gray-500/20 transition-all duration-200 hover:scale-105 hover:shadow-md"
        >
          ···
        </button>
      )}
    </div>
  );
};

// Modern Process Step Card
const ProcessStep = ({ step, desc, icon, index }) => (
  <div className="group text-center p-4 rounded-xl bg-white/50 dark:bg-gray-800/30 backdrop-blur-sm border border-gray-200 dark:border-gray-700 transition-all duration-300 hover:shadow-lg hover:shadow-emerald-500/10 hover:scale-105 hover:-translate-y-1 cursor-pointer">
    <div className="relative">
      <div className="absolute inset-0 bg-gradient-to-r from-emerald-500 to-cyan-500 rounded-full opacity-0 group-hover:opacity-20 transition-opacity duration-300 blur-md" />
      <div className="relative text-2xl md:text-3xl text-emerald-500 mb-2 transition-transform duration-300 group-hover:scale-110 group-hover:rotate-6">
        {icon}
      </div>
    </div>
    <div className="text-sm font-semibold text-gray-900 dark:text-white mb-1 group-hover:text-emerald-600 dark:group-hover:text-emerald-400 transition-colors">
      {step}
    </div>
    <div className="text-xs text-gray-600 dark:text-gray-400 group-hover:text-gray-700 dark:group-hover:text-gray-300 transition-colors">
      {desc}
    </div>
  </div>
);

// ==================== MAIN HOME COMPONENT ====================
const Home = () => {
  const [text, setText] = useState("");
  const [cursorVisible, setCursorVisible] = useState(true);
  const [isMobile, setIsMobile] = useState(false);
  const fullText = "Securing the web, one application at a time.";

  // Check if mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    let i = 0;
    const typingInterval = setInterval(() => {
      if (i <= fullText.length) {
        setText(fullText.slice(0, i));
        i++;
      } else {
        clearInterval(typingInterval);
        const cursorInterval = setInterval(() => {
          setCursorVisible((v) => !v);
        }, 500);
        return () => clearInterval(cursorInterval);
      }
    }, 100);
    return () => clearInterval(typingInterval);
  }, []);

  const scrollToSection = useCallback((sectionId) => {
    const element = document.querySelector(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, []);

  const [expandedCategories, setExpandedCategories] = useState({
    webDev: false,
    cybersecurity: false,
    devOps: false,
    graphicDesign: false,
    certifications: false,
  });

  const toggleCategory = useCallback((category) => {
    setExpandedCategories((prev) => ({
      ...prev,
      [category]: !prev[category],
    }));
  }, []);

  const limits = useMemo(
    () => ({
      webDev: 5,
      cybersecurity: 4,
      devOps: 4,
      graphicDesign: 4,
      certifications: 3,
    }),
    [],
  );

  const expertiseAreas = useMemo(
    () => [
      {
        icon: <FaShieldAlt />,
        title: "Cybersecurity",
        desc: "Proactive threat analysis, penetration testing, and secure architecture design.",
        color: "from-emerald-500 to-cyan-500",
        lightBorder: "border-emerald-200",
      },
      {
        icon: <FaBrain />,
        title: "DevOps & Cloud",
        desc: "Streamlining deployment with Docker, AWS, and CI/CD pipelines for reliable infrastructure.",
        color: "from-emerald-500 to-cyan-500",
        lightBorder: "border-emerald-200",
      },
      {
        icon: <FaCode />,
        title: "Web Development",
        desc: "Building responsive, performant React applications with modern JavaScript and Node.js.",
        color: "from-emerald-500 to-cyan-500",
        lightBorder: "border-emerald-200",
      },
      {
        icon: <FaUserShield />,
        title: "UX/UI Design",
        desc: "User-centered design with Figma, creating intuitive interfaces that delight users.",
        color: "from-emerald-500 to-cyan-500",
        lightBorder: "border-emerald-200",
      },
    ],
    [],
  );

  const processSteps = useMemo(
    () => [
      {
        step: "1. Assess",
        desc: "Threat modeling & audit",
        icon: <FaShieldAlt />,
      },
      { step: "2. Design", desc: "Secure architecture", icon: <FaLock /> },
      { step: "3. Develop", desc: "Safe, clean code", icon: <FaCode /> },
      {
        step: "4. Validate",
        desc: "Pen testing & monitoring",
        icon: <FaRegClock />,
      },
    ],
    [],
  );

  const skillCategories = useMemo(
    () => [
      {
        key: "webDev",
        label: "Languages & Frameworks",
        category: "webDev",
        skills: skillsData.webDev,
        limit: limits.webDev,
      },
      {
        key: "cybersecurity",
        label: "Cybersecurity",
        category: "cybersecurity",
        skills: skillsData.cybersecurity,
        limit: limits.cybersecurity,
      },
      {
        key: "devOps",
        label: "DevOps & Cloud",
        category: "devOps",
        skills: skillsData.devOps,
        limit: limits.devOps,
      },
      {
        key: "graphicDesign",
        label: "Graphic Design",
        category: "graphicDesign",
        skills: skillsData.graphicDesign,
        limit: limits.graphicDesign,
      },
    ],
    [limits],
  );

  return (
    <section
      id="home"
      className="relative min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 via-white to-gray-100 dark:from-gray-900 dark:via-gray-950 dark:to-gray-900 overflow-hidden"
    >
      {/* Subtle grid pattern */}
      <div className="absolute inset-0 dark:opacity-5 opacity-10 pointer-events-none">
        <div className="absolute inset-0 bg-grid-pattern" />
      </div>

      {/* Animated gradient orbs - hidden on mobile */}
      {!isMobile && (
        <>
          <div className="absolute top-20 -left-20 w-72 h-72 bg-emerald-300 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob" />
          <div className="absolute top-40 -right-20 w-72 h-72 bg-cyan-300 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob animation-delay-2000" />
          <div className="absolute -bottom-32 left-1/2 w-72 h-72 bg-blue-300 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob animation-delay-4000" />
        </>
      )}

      <div className="relative z-10 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16 lg:py-32">
        
        {/* ==================== DESKTOP VIEW (Original 2-Column Layout) ==================== */}
        {!isMobile && (
          <div className="grid lg:grid-cols-3 gap-8 lg:gap-10 items-start">
            {/* LEFT COLUMN */}
            <div className="lg:col-span-2 space-y-8">
              {/* Hero Section */}
              <div className="space-y-6">
                <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold tracking-tight animate-fade-in-up">
                  <span className="block text-transparent bg-clip-text bg-gradient-to-r from-emerald-600 via-cyan-500 to-blue-600 bg-300% animate-gradient">
                    I build and secure digital experiences
                  </span>
                </h1>

                <div className="text-xl md:text-2xl lg:text-3xl text-gray-700 dark:text-gray-400 font-light animate-fade-in-up animation-delay-200">
                  <span className="font-medium bg-gradient-to-r from-emerald-600 to-cyan-500 bg-clip-text text-transparent">
                    {text}
                  </span>
                  <span
                    className={`ml-2 w-[2px] h-7 md:h-8 bg-gradient-to-b from-emerald-500 to-cyan-500 ${cursorVisible ? "opacity-100" : "opacity-0"} transition-opacity duration-300 inline-block align-middle`}
                  />
                </div>
                <p className="text-xl text-gray-700 dark:text-gray-400 leading-relaxed max-w-3xl">I specialize in SIEM monitoring, log analysis, threat detection, and vulnerability assessment. My experience includes investigating security alerts, analyzing suspicious activity, performing vulnerability scans with Nmap, and applying security best practices across Linux, AWS, and Azure environments. I am passionate about blue team operations, continuous monitoring, and proactive threat defense.
                </p>
              </div>

              {/* What I Do */}
              <div className="space-y-4 animate-fade-in-up animation-delay-600">
                <h3 className="text-xl md:text-2xl font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                  What I Do
                  <div className="h-px flex-1 bg-gradient-to-r from-emerald-500/30 to-transparent"></div>
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {expertiseAreas.map((area, index) => (
                    <ExpertiseCard key={index} {...area} index={index} />
                  ))}
                </div>
              </div>

              {/* How I Work */}
              <div className="space-y-4 animate-fade-in-up animation-delay-800">
                <h3 className="text-xl md:text-2xl font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                  How I Work
                  <div className="h-px flex-1 bg-gradient-to-r from-emerald-500/30 to-transparent"></div>
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {processSteps.map((step, index) => (
                    <ProcessStep key={index} {...step} index={index} />
                  ))}
                </div>
              </div>

              {/* CTA Section */}
              <div className="space-y-4 pt-4 animate-fade-in-up animation-delay-1200">
                <div className="flex flex-col sm:flex-row gap-4">
                  <button
                    onClick={() => scrollToSection("#projects")}
                    className="group relative flex-1 px-6 py-3 bg-gradient-to-r from-emerald-600 to-cyan-500 text-white font-semibold rounded-xl transition-all duration-300 hover:shadow-2xl hover:shadow-emerald-500/40 hover:scale-[1.02] active:scale-[0.98] overflow-hidden"
                  >
                    <div className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/20 to-white/0 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-1000" />
                    <div className="relative flex items-center justify-center gap-2">
                      <FaShieldAlt className="text-base transition-transform duration-300 group-hover:rotate-12" />
                      <span>See My Security Work</span>
                      <FaArrowRight className="text-base transition-all duration-300 group-hover:translate-x-1 group-hover:scale-110" />
                    </div>
                  </button>

                  <button className="group flex-1 px-6 py-3 bg-white dark:bg-gray-800/80 backdrop-blur-sm text-emerald-600 dark:text-cyan-400 font-semibold rounded-xl border-2 border-emerald-400 dark:border-emerald-500/30 transition-all duration-300 hover:shadow-xl hover:scale-[1.02] active:scale-[0.98] hover:border-emerald-500 dark:hover:border-emerald-500/50 hover:bg-emerald-50 dark:hover:bg-emerald-500/10 flex items-center justify-center gap-2">
                    <FaDownload className="text-base transition-transform duration-300 group-hover:-translate-y-1" />
                    <span>Download CV</span>
                  </button>
                </div>
              </div>
            </div>

            {/* RIGHT COLUMN - Profile */}
            <div className="lg:sticky lg:top-8 animate-fade-in-right">
              <ProfileCard 
                waisImage1={waisImage1}
                skillCategories={skillCategories}
                expandedCategories={expandedCategories}
                toggleCategory={toggleCategory}
                limits={limits}
              />
            </div>
          </div>
        )}

        {/* ==================== MOBILE VIEW (Simple Stacked Layout with Margins) ==================== */}
        {isMobile && (
          <div className="space-y-8 mt-8">
            {/* 1. Profile Section */}
            <ProfileCard 
              waisImage1={waisImage1}
              skillCategories={skillCategories}
              expandedCategories={expandedCategories}
              toggleCategory={toggleCategory}
              limits={limits}
              isMobile={true}
            />

            {/* 2. Hero Text */}
            <div className="text-center px-2">
              <h1 className="text-2xl font-bold mb-3 text-transparent bg-clip-text bg-gradient-to-r from-emerald-600 via-cyan-500 to-blue-600">
                I build and secure digital experiences
              </h1>
              <p className="text-emerald-600 dark:text-emerald-400 font-medium text-sm mb-3">
                {text}
                <span className={`ml-1 w-[2px] h-4 bg-gradient-to-b from-emerald-500 to-cyan-500 ${cursorVisible ? "opacity-100" : "opacity-0"} transition-opacity duration-300 inline-block`} />
              </p>
              <p className="text-gray-600 dark:text-gray-400 text-xs leading-relaxed">
                I specialize in SIEM monitoring, log analysis, threat detection, and vulnerability assessment. My experience includes investigating security alerts, analyzing suspicious activity, performing vulnerability scans with Nmap, and applying security best practices across Linux, AWS, and Azure environments.
              </p>
            </div>

            {/* 3. What I Do */}
            <div className="px-2">
              <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-4">What I Do</h3>
              <div className="space-y-3">
                {expertiseAreas.map((area, index) => (
                  <div key={index} className="flex items-start gap-3 p-3 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700">
                    <div className={`p-2 rounded-lg bg-gradient-to-r ${area.color} text-white flex-shrink-0`}>
                      {area.icon}
                    </div>
                    <div>
                      <h4 className="text-sm font-semibold text-gray-900 dark:text-white">{area.title}</h4>
                      <p className="text-xs text-gray-600 dark:text-gray-400 mt-1 leading-relaxed">{area.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* 4. How I Work */}
            <div className="px-2">
              <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-4">How I Work</h3>
              <div className="grid grid-cols-2 gap-3">
                {processSteps.map((step, index) => (
                  <div key={index} className="text-center p-3 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700">
                    <div className="text-emerald-500 text-xl mb-1">{step.icon}</div>
                    <div className="text-sm font-semibold text-gray-900 dark:text-white">{step.step}</div>
                    <div className="text-xs text-gray-600 dark:text-gray-400 mt-1">{step.desc}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* 5. CTA Buttons */}
            <div className="flex flex-col gap-3 px-2 pt-2">
              <button
                onClick={() => scrollToSection("#projects")}
                className="px-4 py-3 bg-gradient-to-r from-emerald-600 to-cyan-500 text-white font-medium rounded-lg flex items-center justify-center gap-2 active:scale-98 transition-transform"
              >
                <FaShieldAlt className="text-sm" />
                <span>See My Security Work</span>
                <FaArrowRight className="text-sm" />
              </button>
              <button className="px-4 py-3 bg-white dark:bg-gray-800 text-emerald-600 dark:text-cyan-400 font-medium rounded-lg border border-emerald-400 dark:border-emerald-500/30 flex items-center justify-center gap-2 active:scale-98 transition-transform">
                <FaDownload className="text-sm" />
                <span>Download CV</span>
              </button>
            </div>
          </div>
        )}
      </div>

      <style jsx>{`
        .bg-grid-pattern {
          background-image:
            linear-gradient(to right, #d1d5db 1px, transparent 1px),
            linear-gradient(to bottom, #d1d5db 1px, transparent 1px);
          background-size: 40px 40px;
        }
        .dark .bg-grid-pattern {
          background-image:
            linear-gradient(to right, #374151 1px, transparent 1px),
            linear-gradient(to bottom, #374151 1px, transparent 1px);
        }

        @keyframes blob {
          0% { transform: translate(0px, 0px) scale(1); }
          33% { transform: translate(30px, -50px) scale(1.1); }
          66% { transform: translate(-20px, 20px) scale(0.9); }
          100% { transform: translate(0px, 0px) scale(1); }
        }
        .animate-blob { animation: blob 7s infinite; }
        .animation-delay-2000 { animation-delay: 2s; }
        .animation-delay-4000 { animation-delay: 4s; }

        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(30px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fade-in-up { animation: fadeInUp 0.6s ease-out forwards; }

        @keyframes fadeInRight {
          from { opacity: 0; transform: translateX(30px); }
          to { opacity: 1; transform: translateX(0); }
        }
        .animate-fade-in-right { animation: fadeInRight 0.6s ease-out forwards; }

        @keyframes gradient {
          0%, 100% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
        }
        .animate-gradient { animation: gradient 3s ease infinite; }
        .bg-300% { background-size: 300% 300%; }

        .animation-delay-200 { animation-delay: 0.2s; }
        .animation-delay-600 { animation-delay: 0.6s; }
        .animation-delay-800 { animation-delay: 0.8s; }
        .animation-delay-1200 { animation-delay: 1.2s; }

        .active\:scale-98:active { transform: scale(0.98); }

        .skills_bg {
          position: absolute;
          inset: 0;
          overflow: hidden;
          opacity: 0.15;
        }
        .bg_items {
          position: absolute;
          animation: float 20s linear infinite;
        }
        .bg_items:nth-child(1) { top: 10%; left: 5%; animation-duration: 25s; }
        .bg_items:nth-child(2) { top: 20%; right: 10%; animation-duration: 30s; animation-delay: -5s; }
        .bg_items:nth-child(3) { bottom: 15%; left: 15%; animation-duration: 22s; animation-delay: -2s; }
        .bg_items:nth-child(4) { top: 30%; right: 20%; animation-duration: 28s; animation-delay: -8s; }
        .bg_items:nth-child(5) { bottom: 25%; right: 25%; animation-duration: 26s; animation-delay: -3s; }
        .bg_items:nth-child(6) { top: 50%; left: 10%; animation-duration: 32s; animation-delay: -10s; }
        .bg_items:nth-child(7) { bottom: 40%; left: 30%; animation-duration: 24s; animation-delay: -6s; }
        .bg_items:nth-child(8) { top: 70%; right: 15%; animation-duration: 29s; animation-delay: -4s; }
        .bg_items:nth-child(9) { top: 15%; left: 40%; animation-duration: 27s; animation-delay: -7s; }
        .bg_items:nth-child(10) { bottom: 60%; right: 35%; animation-duration: 31s; animation-delay: -9s; }

        @keyframes float {
          0% { transform: translateY(0) rotate(0deg); opacity: 0; }
          10% { opacity: 0.5; }
          90% { opacity: 0.5; }
          100% { transform: translateY(-100vh) rotate(360deg); opacity: 0; }
        }
        .bg_items h1 { font-size: 1.5rem; color: white; }
      `}</style>
    </section>
  );
};

// Profile Card Component (used in both desktop and mobile)
const ProfileCard = ({ waisImage1, skillCategories, expandedCategories, toggleCategory, limits, isMobile = false }) => (
  <div className="rounded-2xl bg-gradient-to-b from-white to-white/95 dark:from-gray-900/90 dark:to-gray-800/90 backdrop-blur-xl border border-gray-300 dark:border-gray-700/50 shadow-2xl shadow-emerald-500/5 overflow-hidden transition-all duration-300 hover:shadow-emerald-500/10">
    {/* Profile Header */}
    <div className="relative">
      <div className="relative h-28 sm:h-32 bg-gradient-to-r from-emerald-500 via-cyan-500 to-blue-500">
        <div className="skills_bg">
          <div className="bg_items"><h1><FaShieldAlt /></h1></div>
          <div className="bg_items"><h1><FaLock /></h1></div>
          <div className="bg_items"><h1><BsKeyFill /></h1></div>
          <div className="bg_items"><h1><SiReact /></h1></div>
          <div className="bg_items"><h1><SiJavascript /></h1></div>
          <div className="bg_items"><h1><SiNodedotjs /></h1></div>
          <div className="bg_items"><h1><SiMongodb /></h1></div>
          <div className="bg_items"><h1><FaCode /></h1></div>
          <div className="bg_items"><h1><FaShieldAlt /></h1></div>
          <div className="bg_items"><h1><BsKeyFill /></h1></div>
        </div>
        <div className="absolute inset-0 bg-gradient-to-t from-transparent via-white/5 to-transparent" />
      </div>
      <div className="absolute -bottom-12 left-1/2 transform -translate-x-1/2">
        <div className="relative">
          <div className="absolute -inset-3 bg-gradient-to-r from-emerald-500 to-cyan-500 rounded-full opacity-20 blur-xl" />
          <div className="relative w-28 h-28 sm:w-32 sm:h-32 md:w-36 md:h-36 rounded-full overflow-hidden border-4 border-white dark:border-gray-900/90 shadow-2xl transition-all duration-300 hover:scale-105 cursor-pointer">
            <img src={waisImage1} alt="Wais Nazari" className="w-full h-full object-cover" />
          </div>
          <div className="absolute bottom-2 right-2 w-3.5 h-3.5 bg-emerald-500 rounded-full border-2 border-white dark:border-gray-900 shadow-lg">
            <div className="absolute inset-0 bg-emerald-400 rounded-full animate-ping opacity-75" />
          </div>
        </div>
      </div>
    </div>

    {/* Profile Content */}
    <div className={`${isMobile ? 'pt-16 pb-5 px-4' : 'pt-12 pb-5 px-5'}`}>
      <div className="text-center">
        <h2 className="text-lg sm:text-xl font-bold text-gray-900 dark:text-white">Wais Nazari</h2>
        <div className="inline-flex items-center gap-1.5 px-2 py-1 mt-1 bg-gradient-to-r from-emerald-500/15 to-cyan-500/15 dark:from-emerald-500/10 dark:to-cyan-500/10 rounded-full border border-emerald-200/50 dark:border-gray-700/50">
          <FaAward className="text-emerald-500 text-xs" />
          <span className="text-xs font-medium text-gray-800 dark:text-gray-300">Security Engineer</span>
        </div>
      </div>
      <div className="mt-3 text-center">
        <p className="text-gray-600 dark:text-gray-400 text-xs leading-relaxed">
          Cybersecurity professional focused on Security Operations (SOC), threat detection, and incident response. With a background in frontend development, I combine application knowledge with security expertise to build and protect modern digital experiences.
        </p>
      </div>

      {/* Technical Expertise */}
      <div className="mt-5">
        <div className="flex items-center justify-between mb-3">
          <h4 className="text-xs font-semibold text-gray-900 dark:text-white">Technical Expertise</h4>
          <div className="text-[10px] text-gray-500 dark:text-gray-400">
            {skillCategories.reduce((acc, cat) => acc + cat.skills.length, 0)}+ skills
          </div>
        </div>
        <div className="space-y-3">
          {skillCategories.map((item) => (
            <div key={item.key} className="space-y-1.5">
              <div className="flex items-center justify-between">
                <span className="text-[11px] font-medium text-gray-700 dark:text-gray-300">{item.label}</span>
                {item.skills.length > item.limit && (
                  <button onClick={() => toggleCategory(item.key)} className="text-[10px] text-emerald-600 dark:text-cyan-400 hover:underline">
                    {expandedCategories[item.key] ? "Show less" : `Show all ${item.skills.length}`}
                  </button>
                )}
              </div>
              <SkillsTags
                skills={expandedCategories[item.key] ? item.skills : item.skills.slice(0, item.limit)}
                category={item.category}
                hasMore={item.skills.length > item.limit}
                expanded={expandedCategories[item.key]}
                onToggle={() => toggleCategory(item.key)}
              />
            </div>
          ))}
        </div>
      </div>

      {/* Certifications */}
      <div className="mt-5 pt-4 border-t border-gray-300 dark:border-gray-700/50">
        <div className="flex items-center justify-between mb-3">
          <h4 className="text-xs font-semibold text-gray-900 dark:text-white">Certifications</h4>
          {certificationsData.length > limits.certifications && (
            <button onClick={() => toggleCategory("certifications")} className="text-[10px] text-emerald-600 dark:text-cyan-400 hover:underline">
              {expandedCategories.certifications ? "Show less" : `Show all ${certificationsData.length}`}
            </button>
          )}
        </div>
        <CertificationsList
          certifications={
            expandedCategories.certifications
              ? certificationsData
              : certificationsData.slice(0, limits.certifications)
          }
        />
      </div>

      {/* Availability */}
      <div className="mt-5 p-3 rounded-lg bg-gradient-to-r from-emerald-500/15 via-cyan-500/15 to-blue-500/15 dark:from-emerald-500/10 dark:via-cyan-500/10 dark:to-blue-500/10 border border-emerald-300 dark:border-emerald-500/20">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-xs font-semibold text-gray-900 dark:text-white">Open for Collaboration</div>
            <div className="text-[10px] text-gray-600 dark:text-gray-400">Remote • Contract or Full-time</div>
          </div>
          <div className="flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-gradient-to-r from-emerald-500/20 to-cyan-500/20">
            <div className="w-1.5 h-1.5 bg-emerald-500 rounded-full animate-pulse" />
            <span className="text-[10px] font-semibold text-emerald-600 dark:text-cyan-400">Available</span>
          </div>
        </div>
      </div>
    </div>
  </div>
);

export default Home;